import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo, useCallback } from "react"
import { Alert, Spinner } from "reactstrap"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"

import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "../../components/Common/TableContainer"
import { withTranslation } from "react-i18next"
import { del } from "helpers/api_helper"
import { getAllPages } from "helpers/api_pagination_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import useCurrentPermissions from "hooks/useCurrentPermissions"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import "../../components/Common/datatables.scss"

const LineSettingList = props => {
  const permissions = useCurrentPermissions("Unable to verify LINE account permissions.")
  const canCreate = permissions.hasScope("systemsettings:linesetting:create")
  const canUpdate = permissions.hasScope("systemsettings:linesetting:update")
  const canDelete = permissions.hasScope("systemsettings:linesetting:delete")
  const createDenied = !permissions.loading && !permissions.error && !canCreate
  const deleteRow = useCallback(
    id => () => {
      if (!canDelete) return
      if (confirm("Are you sure?") == true) {
        const doDelete = async () => {
          try {
            await del(
              process.env.REACT_APP_BACKEND_URL +
                `/v1/admin/line-oa/${id}`
            )
            setLineList(prev => prev.filter(item => item.id !== id))
          } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to delete LINE account."))
          }
        }
        doDelete()
      }
    },
    [canDelete]
  )

  const columns = useMemo(
    () => [
      {
        header: "#",
        enableColumnFilter: false,
        enableSorting: true,
        cell: cellProps => {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              {parseInt(cellProps.row.id) + 1}
            </div>
          )
        },
      },
      {
        header: "Line OA Account",
        accessorKey: "oa_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Tools",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: cellProps => {
          let _lineID = cellProps.row.original.id
          return (
            <>
              <Link
                aria-label="LINE account history"
                className={`btn btn-info`}
                to={`/systemsettings/linesetting/history/${_lineID}`}
              >
                <i className={`bx bx-history`}></i>
              </Link>
              &nbsp;
              {canUpdate && (
                <Link
                  aria-label="Edit LINE account"
                  className={`btn btn-warning`}
                  to={`/systemsettings/linesetting/edit/${_lineID}`}
                >
                  <i className={`bx bx-edit-alt`}></i>
                </Link>
              )}
              &nbsp;
              {canDelete && (
                <button
                  type="button"
                  aria-label="Delete LINE account"
                  className={`btn btn-danger`}
                  onClick={deleteRow(_lineID)}
                >
                  <i className={`bx bx-trash`}></i>
                </button>
              )}
            </>
          )
        },
      },
    ],
    [canUpdate, canDelete, deleteRow]
  )

  const [lineList, setLineList] = useState([])
  const [lineListLoading, setLineListLoading] = useState(true)
  const [loadError, setLoadError] = useState("")

  useEffect(() => {
    setLineListLoading(true)
    const fetchLineList = async () => {
      try {
        const result = await getAllPages(
          process.env.REACT_APP_BACKEND_URL +
            "/v1/admin/line-oa"
        )
        setLineList(result)
      } catch (err) {
        setLoadError(getApiErrorMessage(err, "Unable to load LINE accounts."))
      } finally {
        setLineListLoading(false)
      }
    }
    fetchLineList()
  }, [])

  document.title = "Line Account Setting | " + process.env.REACT_APP_CMS_TITLE

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="System Setting" breadcrumbItem="Line Account Setting" />

        <div className="bInnerContent">
          <div className="d-flex flex-column align-items-end mb-3">
            {canCreate ? (
              <Link className="btn btn-primary" to="/systemsettings/linesetting/create">Create</Link>
            ) : (
              <button type="button" className="btn btn-primary" disabled aria-describedby={createDenied ? "line-account-create-denied" : undefined}>Create</button>
            )}
            {createDenied && <small id="line-account-create-denied" className="text-muted mt-1">Your account does not have permission to create a LINE account.</small>}
          </div>
          <CampaignPermissionNotice permissions={permissions} permissionLabel="LINE account" />
          {loadError && <Alert color="danger">{loadError}</Alert>}
          {lineListLoading ? (
            <Spinner color="primary" />
          ) : !loadError && (
            <TableContainer
              isAddButton={false}
              isExportButton={false}
              columns={columns}
              data={lineList || []}
              isGlobalFilter={true}
              isPagination={true}
              SearchPlaceholder="Search"
              pagination="pagination"
              paginationWrapper="dataTables_paginate paging_simple_numbers"
              tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
              rootMenu="systemsettings"
              subMenu="linesetting"
              isCustomPageSize={true}
            />
          )}
        </div>
      </div>
    </div>
  )
}

LineSettingList.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(LineSettingList)
